// The pipeline graph - the run's primary navigation surface. Phase nodes are
// rectangles, gates are diamonds ("your call"), research lanes render as
// stacked parallel branches, the bounded revision loop shows as a badge.
// Every node routes to its artifact or gate surface.

import { useNavigate, useParams } from "react-router-dom";
import type { GateNumber, RunDetail } from "@ideacrunch/shared";
import { useVocab } from "../vocab";

interface NodeSpec {
  key: string;
  label: string;
  kind?: string; // artifact kind that backs this node
  gate?: GateNumber;
}

const SEQUENCE: NodeSpec[] = [
  { key: "intake", label: "Framing", kind: "intake" },
  { key: "g0", label: "0", gate: 0 },
  { key: "plan", label: "Plan", kind: "plan" },
  { key: "lanes", label: "" }, // placeholder - rendered as the lane stack
  { key: "skeptic", label: "Skeptic", kind: "skeptic-review" },
  { key: "merge", label: "Recommendation", kind: "recommendation" },
  { key: "g1", label: "1", gate: 1 },
  { key: "blueprint", label: "Roadmap", kind: "implementation-plan" },
  { key: "skeptic2", label: "Skeptic #2", kind: "plan-review" },
  { key: "g2", label: "2", gate: 2 },
  { key: "next-steps", label: "Next steps", kind: "next-steps" },
  { key: "archive", label: "Debrief", kind: "residue" },
];

export function Pipeline({ run }: { run: RunDetail }) {
  const navigate = useNavigate();
  const params = useParams();
  const selectedPath = params["*"] ?? "";
  const { gateTitle } = useVocab();

  const artifactByKind = new Map(
    run.artifacts.map((a) => [a.kind as string, a]),
  );

  // Mode C runs grow a Scout node ahead of Framing; other runs keep the
  // canonical 11-node shape. Runs archived before the next-steps artifact
  // existed drop that node instead of showing it as forever-upcoming.
  const legacyArchive =
    artifactByKind.has("residue") && !artifactByKind.has("next-steps");
  const base = legacyArchive
    ? SEQUENCE.filter((s) => s.kind !== "next-steps")
    : SEQUENCE;
  const sequence =
    run.mode === "C" || artifactByKind.has("scout")
      ? [{ key: "scout", label: "Scout", kind: "scout" }, ...base]
      : base;

  const openArtifact = (relPath: string) =>
    navigate(`/runs/${run.id}/a/${relPath}`);

  const baseState = (spec: NodeSpec): string => {
    if (spec.gate !== undefined) {
      if (run.pendingGate === spec.gate) return "needs-you";
      const g = run.gates.find((x) => x.gate === spec.gate);
      return g && g.decisionRaw ? "done" : "upcoming";
    }
    if (spec.key === "lanes") {
      return run.lanes.length > 0 && run.lanes.every((l) => l.artifactPath)
        ? "done"
        : "upcoming";
    }
    return spec.kind && artifactByKind.has(spec.kind) ? "done" : "upcoming";
  };

  // While the engine works, the first not-yet-done step is the one being
  // built - pulse it so waiting reads as progress, not silence.
  const engineActive = run.engine === "running";
  const runningKey = engineActive
    ? sequence.find((s) => baseState(s) === "upcoming")?.key ?? null
    : null;

  const nodeState = (spec: NodeSpec): string => {
    const s = baseState(spec);
    if (s === "upcoming" && spec.key === runningKey) return "running";
    return s;
  };

  return (
    <div className="pipeline-wrap">
      <div className="pipeline">
        {sequence.map((spec, i) => {
          const link = i > 0 ? <span className="p-link" key={`l${i}`} /> : null;

          if (spec.key === "lanes") {
            return (
              <span key={spec.key} style={{ display: "contents" }}>
                {link}
                <div className="p-lanes">
                  {run.lanes.map((lane) => (
                    <button
                      key={lane.key}
                      className={`p-lane ${lane.artifactPath ? "done" : "upcoming"}`}
                      title={
                        lane.artifactPath
                          ? `${lane.claimCount} claims`
                          : "not written yet"
                      }
                      onClick={() =>
                        lane.artifactPath && openArtifact(lane.artifactPath)
                      }
                    >
                      {lane.title}
                      {lane.claimCount > 0 ? ` (${lane.claimCount})` : ""}
                    </button>
                  ))}
                  {run.revisionRound > 0 && (
                    <div className="revision-badge">
                      revision {run.revisionRound} done
                    </div>
                  )}
                </div>
              </span>
            );
          }

          const state = nodeState(spec);

          if (spec.gate !== undefined) {
            const gate = spec.gate;
            return (
              <span key={spec.key} style={{ display: "contents" }}>
                {link}
                <button
                  className={`p-node p-gate ${state}`}
                  title={gateTitle(gate)}
                  onClick={() => navigate(`/runs/${run.id}/gate/${gate}`)}
                >
                  <span className="box">
                    <span>{gate}</span>
                  </span>
                  <span className="state">
                    {state === "needs-you" ? "your call" : `gate ${gate}`}
                  </span>
                </button>
              </span>
            );
          }

          const artifact = spec.kind
            ? artifactByKind.get(spec.kind)
            : undefined;
          const selected =
            artifact !== undefined && selectedPath === artifact.relPath;
          return (
            <span key={spec.key} style={{ display: "contents" }}>
              {link}
              <button
                className={`p-node ${state} ${selected ? "selected" : ""}`}
                onClick={() => artifact && openArtifact(artifact.relPath)}
                title={artifact?.relPath ?? "not written yet"}
              >
                <span className="box">{spec.label}</span>
                <span className="state">
                  {state === "done" ? "" : "upcoming"}
                </span>
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
